/**
 * Credentials client — mirrors `apps/api/src/domains/supervision/routes.ts`
 * verbatim (credentials shares that route file's HTTP surface; see its header
 * for the full permission model):
 *
 *   credential:read        — see credentials (self-narrowed unless you hold
 *                             `:manage` or `:verify`)
 *   credential:manage      — file and renew
 *   credential:verify      — accept or reject somebody's evidence (never your
 *                             own — the server refuses that regardless of role)
 *   credential:type:manage — change the catalogue and requirements
 *
 * `verificationMeaning` travels on every shape that carries a verification, and
 * the UI must show it wherever a verified state is shown: Phibian holds no
 * integration with any licensing board or registry, and a green tick implying
 * otherwise would be the wrong kind of untruth. Never hardcode a paraphrase —
 * always render the string the server sent.
 */
import { get, post } from './transport.js';
/** `GET /credentials/overview`. */
export function overview(options) {
    return get('/credentials/overview', options);
}
/** `GET /credentials/workers` — the workforce credential list, scoped server-side. */
export function workers(query = {}, options) {
    return get('/credentials/workers', { ...options, query: { ...query } });
}
/** `GET /credentials/workers/:workerId` — self, or org-wide with `:manage`/`:verify`. */
export function workerDetail(workerId, options) {
    return get(`/credentials/workers/${workerId}`, options);
}
/** `GET /credentials/types`. */
export async function types(query = {}, options) {
    const body = await get('/credentials/types', {
        ...options,
        query: { ...query },
    });
    return body.types;
}
/** `POST /credentials/types` — needs `credential:type:manage`. */
export async function createType(input, options) {
    const body = await post('/credentials/types', input, options);
    return body.type;
}
/** `POST /credentials/types/:id/retire`. */
export async function retireType(id, options) {
    const body = await post(`/credentials/types/${id}/retire`, {}, options);
    return body.type;
}
/** `GET /credentials/requirements`. */
export async function requirements(options) {
    const body = await get('/credentials/requirements', options);
    return body.requirements;
}
/** `POST /credentials/requirements` — needs `credential:type:manage`. */
export async function createRequirement(input, options) {
    const body = await post('/credentials/requirements', input, options);
    return body.requirement;
}
/**
 * `POST /credentials/requirements/:id/amend` — closes the existing window and
 * opens a new one; the credential type cannot change (it is the requirement's
 * identity), matching the server's rule.
 */
export function amendRequirement(id, input, options) {
    return post(`/credentials/requirements/${id}/amend`, input, options);
}
/** `POST /credentials/requirements/:id/retire`. */
export async function retireRequirement(id, options) {
    const body = await post(`/credentials/requirements/${id}/retire`, {}, options);
    return body.requirement;
}
/**
 * `POST /credentials` — files a credential. ALWAYS lands `pending_verification`
 * server-side, even when an administrator files it on somebody's behalf —
 * nothing self-verifies.
 */
export async function fileCredential(input, options) {
    const body = await post('/credentials', input, options);
    return body.credential;
}
/**
 * `POST /credentials/:id/renew` — files a replacement and supersedes the
 * predecessor, which keeps its own dates and verification as history.
 */
export async function renewCredential(id, input, options) {
    const body = await post(`/credentials/${id}/renew`, input, options);
    return body.credential;
}
/**
 * `POST /credentials/:id/verify` — needs `credential:verify`. The server
 * refuses this when the caller is the credential's own owner, regardless of
 * role (separation of duties) — surface that refusal message as-is.
 */
export async function verifyCredential(id, options) {
    const body = await post(`/credentials/${id}/verify`, {}, options);
    return body.credential;
}
/** `POST /credentials/:id/reject` — needs `credential:verify`; same self-reject refusal as verify. */
export async function rejectCredential(id, reason, options) {
    const body = await post(`/credentials/${id}/reject`, { reason }, options);
    return body.credential;
}
//# sourceMappingURL=credentials.js.map